// src/App.js
import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Toaster } from 'react-hot-toast';
import { AppProvider, useApp } from './context/AppContext';
import { PlatformProvider } from './context/PlatformContext';
import { LanguageProvider, useLanguage } from './i18n/LanguageContext';
import { listenToForegroundMessages, logout, initNativePhoneAuth } from './services/firebase';
import { notifyOrderUpdate } from './utils/alertSound';
import { buildWhatsappUrl } from './utils/support';

// Eager: shown immediately on first paint.
import Splash from './pages/Splash';
import Login from './pages/Login';

// Lazy: each route becomes its own chunk so the first screen loads fast.
const Home = lazy(() => import('./pages/Home'));
const Grocery = lazy(() => import('./pages/Grocery'));
const Cart = lazy(() => import('./pages/Cart'));
const Orders = lazy(() => import('./pages/Orders'));
const OrderTracking = lazy(() => import('./pages/OrderTracking'));
const Profile = lazy(() => import('./pages/Profile'));
const Addresses = lazy(() => import('./pages/Addresses'));
const Payments = lazy(() => import('./pages/Payments'));
const Help = lazy(() => import('./pages/Help'));
const NotifyMe = lazy(() => import('./pages/NotifyMe'));

// Lazy: these pull in Google Maps — only load them when a sheet actually opens.
const DeliveryLocationSheet = lazy(() => import('./components/DeliveryLocationSheet'));
const OutOfServiceArea = lazy(() => import('./components/OutOfServiceArea'));
const DeliveryPinSheet = lazy(() => import('./components/DeliveryPinSheet'));
import { GoogleMapsPreloader } from './hooks/useGoogleMapsLoader';

/** Warm the chunks a user is most likely to open next, once the app is idle. */
const prefetchCommonRoutes = () => {
  const run = () => {
    import('./pages/Grocery');
    import('./pages/Cart');
    import('./pages/Orders');
    import('./pages/OrderTracking');
  };
  if (typeof window !== 'undefined' && window.requestIdleCallback) {
    window.requestIdleCallback(run, { timeout: 3000 });
  } else {
    setTimeout(run, 1500);
  }
};

const DeliveryPinHost = () => {
  const { pinSheetOpen, pinSheetSeed, closeDeliveryPinSheet } = useApp();
  if (!pinSheetOpen) return null;
  return (
    <Suspense fallback={null}>
      <DeliveryPinSheet
        open
        onClose={closeDeliveryPinSheet}
        seedAddress={pinSheetSeed}
      />
    </Suspense>
  );
};

const DeliveryLocationHost = () => {
  const {
    deliverySheetOpen,
    deliverySheetRequired,
    closeDeliverySheet,
    outOfServiceAreaOpen,
    dismissOutOfServiceArea,
  } = useApp();
  if (!deliverySheetOpen && !outOfServiceAreaOpen) return null;
  return (
    <Suspense fallback={null}>
      {deliverySheetOpen && (
        <DeliveryLocationSheet
          open
          onClose={closeDeliverySheet}
          required={deliverySheetRequired}
        />
      )}
      {outOfServiceAreaOpen && (
        <OutOfServiceArea open onChangeLocation={dismissOutOfServiceArea} />
      )}
    </Suspense>
  );
};

const FcmListener = () => {
  const navigate = useNavigate();
  useEffect(() => {
    let unsub = () => {};
    listenToForegroundMessages((payload) => {
      const title = payload.notification?.title || payload.data?.title;
      const body = payload.notification?.body || payload.data?.body;
      const orderId = payload.data?.orderId;
      if (title || body) {
        notifyOrderUpdate(title || 'Order update', body);
        toast(`${title || 'Order update'}${body ? ` — ${body}` : ''}`, { duration: 6000, icon: '🔔' });
      }
      if (orderId && payload.data?.type === 'order_status') {
        navigate(`/track/${orderId}`);
      }
    }).then((u) => { unsub = u; });
    return () => unsub();
  }, [navigate]);
  return null;
};

const BlockedAccount = () => {
  const { t } = useLanguage();
  return (
    <div className="app-shell app-shell--white" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🚫</div>
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>{t('blocked.title')}</h1>
      <p className="text-muted" style={{ maxWidth: 320, marginBottom: 24, lineHeight: 1.5 }}>{t('blocked.message')}</p>
      <a href={buildWhatsappUrl('My account is blocked — please help')} className="btn btn-green" style={{ marginBottom: 12, textDecoration: 'none' }}>{t('blocked.contact')}</a>
      <button type="button" className="btn btn-ghost" onClick={() => logout()}>{t('blocked.logout')}</button>
    </div>
  );
};

const AppRoutes = () => {
  const { user, authLoading, profileReady, needsName, userProfile } = useApp();
  const location = useLocation();
  const onLogin = location.pathname === '/login';

  if (!onLogin && (authLoading || (user && !profileReady))) return <Splash />;
  if (user && profileReady && userProfile?.isBlocked) return <BlockedAccount />;

  const gate = (page) => {
    if (!user) return <Navigate to="/login" replace />;
    if (needsName) return <Navigate to="/login" replace />;
    return page;
  };

  return (
    <>
      {user && profileReady && !needsName && !userProfile?.isBlocked && <GoogleMapsPreloader />}
      {user && profileReady && !needsName && !userProfile?.isBlocked && <DeliveryLocationHost />}
      {user && profileReady && !needsName && !userProfile?.isBlocked && <DeliveryPinHost />}
      <Suspense fallback={<Splash />}>
      <Routes>
      <Route
        path="/login"
        element={
          !user ? (
            <Login />
          ) : needsName ? (
            <Login startAt="name" />
          ) : profileReady ? (
            <Navigate to="/" replace />
          ) : (
            <Splash />
          )
        }
      />
      <Route path="/" element={gate(<Home />)} />
      <Route path="/grocery" element={gate(<Grocery />)} />
      <Route path="/orders" element={gate(<Orders />)} />
      <Route path="/cart" element={gate(<Cart />)} />
      <Route path="/track/:orderId" element={gate(<OrderTracking />)} />
      <Route path="/profile" element={gate(<Profile />)} />
      <Route path="/addresses" element={gate(<Addresses />)} />
      <Route path="/payments" element={gate(<Payments />)} />
      <Route path="/help" element={gate(<Help />)} />
      <Route path="/notify/:service" element={gate(<NotifyMe />)} />
      <Route path="/rides" element={<Navigate to="/notify/rides" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    </>
  );
};

export default function App() {
  useEffect(() => {
    initNativePhoneAuth();
    prefetchCommonRoutes();
  }, []);

  return (
    <LanguageProvider>
      <PlatformProvider>
      <AppProvider>
        <BrowserRouter>
          <FcmListener />
          <a href="#main-content" className="skip-link">Skip to main content</a>
          <AppRoutes />
          <Toaster
            position="top-center"
            toastOptions={{
              style: {
                fontFamily: 'Sora, sans-serif',
                fontSize: '14px',
                fontWeight: 600,
                borderRadius: '14px',
                padding: '12px 18px',
                background: '#0d1117',
                color: '#f9fafb',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              },
              success: { iconTheme: { primary: '#00A86B', secondary: '#0d1117' } },
              error: { iconTheme: { primary: '#ef4444', secondary: '#0d1117' } },
            }}
          />
        </BrowserRouter>
      </AppProvider>
      </PlatformProvider>
    </LanguageProvider>
  );
}
